import React, { useState, useCallback, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import WelcomePage from './pages/WelcomePage';
import api from './utils/api';
import sessionDB from './utils/sessionDB';
import AppRoutes from './router/AppRoutes';
import i18n from './i18n';

/**
 * Rehydrate session state from sessionDB on first render so a page refresh
 * (or opening a deep link like /hrms/attendance directly) doesn't kick a
 * signed-in user back to the login page. sessionStorage — and therefore
 * sessionDB — already survives refreshes within the same tab; this just
 * makes App's React state agree with it on load.
 */
function useSession() {
  const [session, setSession] = useState(() => {
    const existing = sessionDB.getSession();
    return existing.accessToken ? existing : null;
  });

  const handleLogin = useCallback((data) => {
    api.setSession(data);
    setSession(data);
  }, []);

  const handleLogout = useCallback(() => {
    api.clearSession();
    setSession(null);
  }, []);

  return { session, handleLogin, handleLogout };
}

/** Gate a subtree behind an active session, bouncing to the base URL otherwise. */
function RequireAuth({ session, children }) {
  return session ? children : <Navigate to="/" replace />;
}

export default function App() {
  const { session, handleLogin, handleLogout } = useSession();

  useEffect(() => {
    const theme = sessionDB.getEmpPreferences().theme?.toLowerCase() === 'dark'
      ? 'dark'
      : 'light';

    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;

    return () => {
      delete document.documentElement.dataset.theme;
      document.documentElement.style.colorScheme = '';
    };
  }, [session]);

  useEffect(() => {
    const setDocumentLanguage = (language) => {
      document.documentElement.lang = ["hi", "te", "kn"].includes(language) ? language : "en";
    };

    setDocumentLanguage(i18n.resolvedLanguage);
    i18n.on("languageChanged", setDocumentLanguage);
    return () => i18n.off("languageChanged", setDocumentLanguage);
  }, []);

  useEffect(() => {
    const onLogout = () => handleLogout();
    window.addEventListener("hrms-logout", onLogout);
    return () => window.removeEventListener("hrms-logout", onLogout);
  }, [handleLogout]);

  return (
    <HashRouter>
      <Routes>
        <Route
          path="/login"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/"
          element={session ? <WelcomePage data={session} onLogout={handleLogout} /> : <LoginPage onLogin={handleLogin} />}
        />
        <Route
          path="/*"
          element={
            <RequireAuth session={session}>
              <AppRoutes />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  );
}
